import {useCallback, useEffect, useState} from 'react';

import {useAnnotationModeStore} from './AnnotationModeStore';
import type {HighlightRange} from './annotationUtils';
import {
  clearHighlights,
  getSelectionOffsets,
  hasUntypesetMath,
  isHighlightApiSupported,
  observeSubtreeChanges,
  paintHighlights
} from './annotationUtils';

const EMPTY_HIGHLIGHTS: HighlightRange[] = [];

/**
 * Captures selections as stored offsets and paints stored highlights via the
 * Custom Highlight API. `containerRef` is a callback ref so the paint effect
 * re-runs when the element attaches a render late (e.g. the feature flag
 * resolving after first render). Painting runs whenever highlights exist;
 * callers gate `onMouseUp` on mode.
 */
export function useAnnotationsHighlights(questionId: string): {
  onMouseUp: () => void;
  containerRef: (element: HTMLElement | null) => void;
} {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const highlights = useAnnotationModeStore(
    (state) => state.highlightsByQuestionId[questionId] ?? EMPTY_HIGHLIGHTS
  );
  const addHighlight = useAnnotationModeStore((state) => state.addHighlight);

  const onMouseUp = useCallback(() => {
    if (!container) {
      return;
    }
    const offsets = getSelectionOffsets(container);
    if (!offsets) {
      return;
    }
    addHighlight(questionId, offsets);
    // Clear the native selection so it is replaced by the painted highlight.
    window.getSelection()?.removeAllRanges();
  }, [addHighlight, container, questionId]);

  useEffect(() => {
    if (!container || highlights.length === 0 || !isHighlightApiSupported()) {
      return undefined;
    }
    const repaint = () => paintHighlights(container, highlights);
    // Paint now unless MathJax will still rewrite the prompt — in that case the
    // observer paints once it settles, avoiding a flash against the stale DOM.
    if (!hasUntypesetMath(container)) {
      repaint();
    }
    // Highlight Ranges are live, so a painted highlight would slide as MathJax
    // rewrites the prompt. Clear on mutation, repaint once the DOM settles.
    const stopObserving = observeSubtreeChanges(container, {
      onMutate: clearHighlights,
      onSettle: repaint
    });
    return () => {
      stopObserving();
      clearHighlights();
    };
  }, [container, highlights]);

  return {onMouseUp, containerRef: setContainer};
}
